import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import { HtmlOverlay3D } from '@asset-viz/objects/decor/HtmlOverlay3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import { useSurfaceSample } from '@asset-viz/objects/surfaces/util/useSurfaceSample'
import {
  HtmlOverlayAnchor,
  HtmlOverlayPriority,
} from '@asset-viz/utils/helpers'
import React, { ReactNode } from 'react'
import styled from 'styled-components'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentCoords,
  GeckoFeature,
} from 'types'

export const LabelContainer = styled.div<{
  $backgroundColor?: string
  $screenshotMode?: boolean
}>`
  display: flex;
  word-wrap: break-word;
  white-space: break-spaces;

  width: 100%;
  max-width: 100px;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;

  pointer-events: none;
  padding: 8px 0;

  border-radius: 0.25rem;
  padding: ${({ $screenshotMode }) =>
    $screenshotMode ? '0.1rem 0.1rem' : '0.25rem 0.5rem'};

  background-color: ${({ $backgroundColor }) => $backgroundColor};
  opacity: ${({ $screenshotMode }) => ($screenshotMode ? '0.8' : '1')};
`

export const Label = styled.small<{
  $isSelected: boolean
  $textColor: string
  $screenshotMode?: boolean
}>`
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.$isSelected ? 8 : 2)} !important;
  -webkit-box-orient: vertical;

  overflow: clip;
  word-wrap: break-word;
  white-space: break-spaces;

  user-select: none;
  cursor: default;
  text-align: center;
  color: ${({ $textColor }) => $textColor};
  opacity: unset;
  font-size: ${({ $screenshotMode }) => ($screenshotMode ? '24px' : '12px')};
`

const getAnchorForBoundary = (
  anchor: HtmlOverlayAnchor,
  wrapMode: ComponentSurfaceWrappingMode,
  transpose: boolean // mostly for pipes
) => {
  if (
    (!transpose &&
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX) ||
    (transpose &&
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY)
  ) {
    switch (anchor) {
      case 'top':
        return 'top'
      case 'bottom':
        return 'bottom'
      case 'right':
        return 'left'
      case 'left':
        return 'right'
      case 'top right':
        return 'top left'
      case 'top left':
        return 'top right'
      case 'bottom left':
        return 'bottom right'
      case 'bottom right':
        return 'bottom left'
      default:
        return anchor
    }
  } else if (
    (!transpose &&
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY) ||
    (transpose &&
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX)
  ) {
    switch (anchor) {
      case 'top':
        return 'bottom'
      case 'bottom':
        return 'top'
      case 'right':
        return 'right'
      case 'left':
        return 'left'
      case 'top right':
        return 'bottom right'
      case 'top left':
        return 'bottom left'
      case 'bottom left':
        return 'top left'
      case 'bottom right':
        return 'top right'
      default:
        return anchor
    }
  } else return anchor
}

/**
 * A component used to display a feature's name on a component's surface
 * @param feature           The feature being named
 * @param coords            The coordinates of this label in 2D component space
 * @param anchor            The HTML anchor used of this label
 * @param showBoundaryLabelIn2D If the coordinates are on a boundary of a component that wraps,
 *                            show the name label on both sides of the boundary in 2D
 * @param transpose         This will transpose the label anchor ( for if the component is a pipe )
 * @constructor
 */
export const FeatureLabel3D: React.FC<{
  feature: GeckoFeature
  coords: GeckoComponentCoords
  anchor: HtmlOverlayAnchor
  showBoundaryLabelIn2D?: boolean
  transpose?: boolean
  className?: string
  scale?: [number, number, number]
}> = ({
  feature,
  coords,
  anchor,
  showBoundaryLabelIn2D = false,
  transpose = false,
  className,
  scale = [1, 1, 1],
}) => {
  //----------------------------------------------------------------------------

  const {
    showFeatures,
    showFeatureLabels,
    selection,
    show3D,
    labelBackgroundColorString,
    featureLabelColorString,
    mode,
  } = useAssetVizInternal()
  const isScreenshotMode = mode === AssetVizMode.Screenshot
  const { wrapMode, componentModel } = useSurfaceProps()
  const { position, boundaryPosition } = useSurfaceSample(coords)
  const featureName = (
    'name' in feature ? feature.name : undefined
  ) as ReactNode

  const boundaryAnchor = React.useMemo(
    () => getAnchorForBoundary(anchor, wrapMode, transpose),
    [wrapMode, anchor, transpose]
  )
  const showThisFeatureLabel = React.useMemo(
    () =>
      (typeof showFeatureLabels === 'boolean'
        ? showFeatureLabels
        : showFeatureLabels({ feature, componentModel })) &&
      (typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature, componentModel })),
    [feature, componentModel, showFeatureLabels, showFeatures]
  )

  return !position || !featureName ? null : (
    <>
      <HtmlOverlay3D
        x={position.x * scale[0]}
        y={position.y * scale[1]}
        z={position.z * scale[2]}
        anchor={anchor}
        priority={HtmlOverlayPriority.FeatureNamePlate}
        visible={showThisFeatureLabel}>
        <LabelContainer
          $backgroundColor={labelBackgroundColorString}
          $screenshotMode={isScreenshotMode}>
          <Label
            className={className}
            $isSelected={feature === selection}
            $textColor={featureLabelColorString}
            $screenshotMode={isScreenshotMode}>
            {featureName}
          </Label>
        </LabelContainer>
      </HtmlOverlay3D>

      {showBoundaryLabelIn2D && boundaryPosition !== undefined ? (
        <HtmlOverlay3D
          x={boundaryPosition.x}
          y={boundaryPosition.y}
          z={boundaryPosition.z}
          anchor={boundaryAnchor}
          priority={HtmlOverlayPriority.FeatureNamePlate}
          visible={showThisFeatureLabel && !show3D}>
          <LabelContainer
            $backgroundColor={labelBackgroundColorString}
            $screenshotMode={isScreenshotMode}>
            <Label
              className={className}
              $isSelected={feature === selection}
              $textColor={featureLabelColorString}
              $screenshotMode={isScreenshotMode}>
              {featureName}
            </Label>
          </LabelContainer>
        </HtmlOverlay3D>
      ) : undefined}
    </>
  )
}
